import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Modal } from 'react-native';

export default function AIAssistantPanel({ recommendations, completedTasks, ignoredTasks, onToggleTask }) {
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAdvice, setShowAdvice] = useState(false);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return '#ff6b6b';
      case 'medium': return '#ffa500';
      case 'low': return '#4a9eff';
      default: return '#666';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'high': return 'High Priority';
      case 'medium': return 'Medium';
      case 'low': return 'Nice to Have';
      default: return '';
    }
  };

  const visibleRecommendations = recommendations.filter((_, idx) => 
    !ignoredTasks.includes(idx)
  );

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>💡 AI Suggestions</Text>
        <Text style={styles.count}>
          {completedTasks.length}/{visibleRecommendations.length} completed
        </Text>
      </View>

      <ScrollView style={styles.taskList}>
        {recommendations.map((rec, index) => {
          if (ignoredTasks.includes(index)) return null;
          
          const isCompleted = completedTasks.includes(index);
          const severityColor = getSeverityColor(rec.severity);

          return (
            <View 
              key={index} 
              style={[
                styles.taskCard,
                isCompleted && styles.taskCardCompleted
              ]}
            >
              <TouchableOpacity
                style={styles.checkbox}
                onPress={() => onToggleTask(index, isCompleted ? 'undo' : 'complete')}
              >
                {isCompleted && <Text style={styles.checkmark}>✓</Text>}
              </TouchableOpacity>

              <View style={styles.taskContent}>
                <View style={styles.taskHeader}>
                  <Text style={[styles.taskTitle, isCompleted && styles.taskTitleCompleted]}>
                    {rec.task}
                  </Text>
                  <View style={[styles.severityBadge, { backgroundColor: severityColor }]}>
                    <Text style={styles.severityText}>{getSeverityLabel(rec.severity)}</Text>
                  </View>
                </View>

                <Text style={styles.taskReason}>{rec.reason}</Text>

                <View style={styles.taskActions}>
                  <TouchableOpacity
                    onPress={() => {
                      setSelectedTask(rec);
                      setShowAdvice(true);
                    }}
                    style={styles.adviceButton}
                  >
                    <Text style={styles.adviceButtonText}>💬 Get Advice</Text>
                  </TouchableOpacity>

                  {!isCompleted && (
                    <TouchableOpacity
                      onPress={() => onToggleTask(index, 'ignore')}
                      style={styles.ignoreButton}
                    >
                      <Text style={styles.ignoreButtonText}>Ignore</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            </View>
          );
        })}
      </ScrollView>

      {/* Advice Modal */}
      <Modal
        visible={showAdvice}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowAdvice(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>💡 Detailed Advice</Text>
              <TouchableOpacity onPress={() => setShowAdvice(false)}>
                <Text style={styles.modalClose}>✕</Text>
              </TouchableOpacity>
            </View>

            {selectedTask && (
              <>
                <Text style={styles.adviceTask}>{selectedTask.task}</Text>
                <Text style={styles.adviceReason}>{selectedTask.reason}</Text>
                
                <View style={styles.adviceSection}>
                  <Text style={styles.adviceSectionTitle}>How to fix:</Text>
                  <Text style={styles.adviceText}>{selectedTask.advice}</Text>
                </View>

                {selectedTask.location && (
                  <View style={styles.adviceSection}>
                    <Text style={styles.adviceSectionTitle}>Where to look:</Text>
                    <Text style={styles.adviceText}>{selectedTask.location}</Text>
                  </View>
                )}
              </>
            )}

            <TouchableOpacity
              style={styles.modalButton}
              onPress={() => setShowAdvice(false)}
            >
              <Text style={styles.modalButtonText}>Got it!</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
  },
  count: {
    fontSize: 12,
    color: '#4a9eff',
    fontWeight: '600',
  },
  taskList: {
    flex: 1,
  },
  taskCard: {
    flexDirection: 'row',
    backgroundColor: '#0a0a0a',
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#333',
    gap: 10,
  },
  taskCardCompleted: {
    opacity: 0.6,
    borderColor: '#2a5a2a',
    backgroundColor: '#0f1f0f',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#4a9eff',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  checkmark: {
    color: '#4a9eff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  taskContent: {
    flex: 1,
  },
  taskHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
    gap: 10,
  },
  taskTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#fff',
    flex: 1,
    lineHeight: 18,
  },
  taskTitleCompleted: {
    textDecorationLine: 'line-through',
    color: '#888',
  },
  severityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  severityText: {
    fontSize: 10,
    color: '#fff',
    fontWeight: '600',
  },
  taskReason: {
    fontSize: 11,
    color: '#aaa',
    marginBottom: 8,
    lineHeight: 15,
  },
  taskActions: {
    flexDirection: 'row',
    gap: 10,
  },
  adviceButton: {
    backgroundColor: '#4a9eff',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
  },
  adviceButtonText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },
  ignoreButton: {
    backgroundColor: '#333',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
  },
  ignoreButtonText: {
    color: '#aaa',
    fontSize: 11,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.95)',
    justifyContent: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#1a1a1a',
    borderRadius: 12,
    padding: 20,
    maxHeight: '80%',
    borderWidth: 1,
    borderColor: '#333',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  modalClose: {
    fontSize: 24,
    color: '#aaa',
  },
  adviceTask: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 10,
  },
  adviceReason: {
    fontSize: 14,
    color: '#aaa',
    marginBottom: 20,
    lineHeight: 20,
  },
  adviceSection: {
    marginBottom: 20,
  },
  adviceSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4a9eff',
    marginBottom: 8,
  },
  adviceText: {
    fontSize: 14,
    color: '#ccc',
    lineHeight: 20,
  },
  modalButton: {
    backgroundColor: '#4a9eff',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
